import React from 'react';
import * as XLSX from 'xlsx';
import { ticketCategories, getDateShortcut, getCategoryDetail } from '../../utils/viewTicketsUtils';

const ExportModal = ({
  isOpen,
  onClose,
  tickets,
  locationInfo,
  dateRange,
  setDateRange,
  selectedStatuses,
  setSelectedStatuses
}) => {
  if (!isOpen) return null;

  const handleDateShortcut = (days) => {
    setDateRange(getDateShortcut(days));
  };

  const handleStatusChange = (event) => {
    setSelectedStatuses(event.target.value);
  };

  const exportToExcel = () => {
    // Filter tickets based on date range and categories
    const filteredTickets = tickets.filter(ticket => {
      const ticketDate = new Date(ticket.createdAt);
      const startDate = dateRange.startDate ? new Date(dateRange.startDate) : null;
      const endDate = dateRange.endDate ? new Date(dateRange.endDate) : null;
      
      const dateInRange = (!startDate || ticketDate >= startDate) && 
                         (!endDate || ticketDate <= endDate);
      
      const categoryMatch = ticketCategories.length === 0 || 
                          ticketCategories.includes(ticket.category);
      
      const statusMatch = selectedStatuses.length === 0 || 
                         selectedStatuses.includes(ticket.status);

      return dateInRange && categoryMatch && statusMatch;
    });

    // Prepare data for export
    const exportData = filteredTickets.map(ticket => ({
      'Ticket ID': ticket.id,
      'Tracking ID': ticket.trackingId || '',
      'Category': ticket.category || '',
      'Sub Category': getCategoryDetail(ticket, 'subcategory'),
      'Description': getCategoryDetail(ticket, 'description'),
      'Requester Name': ticket.requester?.name || 'Unknown',
      'Requester Email': ticket.requester?.email || 'No email provided',
      'Priority': ticket.priority || '',
      'Status': ticket.status || '',
      'Created At': ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : '',
      'Location': locationInfo?.name || 'Unknown',
      'Location Type': locationInfo?.type || 'Unknown',
      'Message': ticket.details?.message || ticket.details?.documentMessage || '',
      'File Attachment': ticket.details?.fileName || ''
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 10 }, // Ticket ID
      { wch: 15 }, // Tracking ID
      { wch: 20 }, // Category
      { wch: 20 }, // Sub Category
      { wch: 25 }, // Requester Name
      { wch: 30 }, // Requester Email
      { wch: 15 }, // Priority
      { wch: 15 }, // Status
      { wch: 15 }, // Created At
      { wch: 25 }, // Location
      { wch: 20 }, // Location Type
      { wch: 20 }, // Message
      { wch: 20 }, // File Attachment
    ];
    ws['!cols'] = colWidths;

    // Add header styling
    const headerStyle = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "1976D2" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        bottom: { style: "thin" },
        left: { style: "thin" },
        right: { style: "thin" }
      }
    };

    // Apply header styling to all header cells
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellRef = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellRef]) continue;
      ws[cellRef].s = headerStyle;
    }

    // Add alternating row colors
    for (let R = 1; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellRef]) continue;
        ws[cellRef].s = {
          fill: { fgColor: { rgb: R % 2 === 0 ? "F5F5F5" : "FFFFFF" } },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
          }
        };
      }
    }

    // Add filters to the worksheet
    ws['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(range.e.c)}${range.e.r + 1}` };

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tickets');

    // Generate Excel file
    const fileName = `ticket_logs_${locationInfo?.name || 'unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full font-poppins">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Export Settings
          </h3>
        </div>
        <div className="p-6">
          <div className="mt-4">
            {/* Date Range Section */}
            <h4 className="text-base font-medium text-gray-900 mb-4">
              Date Range
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                />
              </div>
              <div className="col-span-2">
                <div className="flex flex-wrap gap-2 mt-2">
                  <button 
                    onClick={() => handleDateShortcut(1)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm font-poppins hover:bg-gray-50"
                  >
                    Last 24h
                  </button>
                  <button 
                    onClick={() => handleDateShortcut(7)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm font-poppins hover:bg-gray-50"
                  >
                    Last Week
                  </button>
                  <button 
                    onClick={() => handleDateShortcut(30)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm font-poppins hover:bg-gray-50"
                  >
                    Last Month
                  </button>
                  <button 
                    onClick={() => handleDateShortcut(365)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm font-poppins hover:bg-gray-50"
                  >
                    Last Year
                  </button>
                </div>
              </div>
            </div>

            {/* Category Filter Section */}
            <h4 className="text-base font-medium text-gray-900 mb-4">
              Ticket Category
            </h4>
            <div className="grid grid-cols-1 gap-4 mb-6">
              <div className="flex flex-col gap-2">
                {ticketCategories.map((category) => (
                  <label key={category} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={ticketCategories.includes(category)}
                      className="rounded border-gray-300"
                    />
                    <span className="font-poppins">{category.replace(/_/g, ' ')}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Status Filter Section */}
            <h4 className="text-base font-medium text-gray-900 mb-4">
              Ticket Status
            </h4>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <select
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-poppins"
                >
                  <option value="" disabled>Select Statuses</option>
                  {['PENDING', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'].map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button 
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-poppins"
          >
            Cancel
          </button>
          <button 
            onClick={exportToExcel}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-poppins"
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;
